import React, { useEffect, useState } from "react";
import {
  Icon,
  Sort,
  AnimatedDropdown,
  SegmentedControl,
  SegmentedControlTab,
  useMediaQuery,
  Button,
  Modal,
  breakpoints,
  Option,
  SortContext
} from "@tvg/design-system";
import {
  RunnerSortByEnum,
  RunnerSortOrderEnum,
  SortOption
} from "../../../types";
import {
  ModalButtonContainer,
  OptionGroup,
  OptionGroupsBox
} from "./styled-components";
import {
  sortOptions as sortOptionsNotHarness,
  sortOptionsHarness,
  SORT_OPTION_DEFAULT
} from "./constants";

export interface RunnerSortProps {
  sortOrder: RunnerSortOrderEnum;
  sortBy: RunnerSortByEnum;
  onSortOrderChange: (order: RunnerSortOrderEnum) => void;
  onSortByChange: (sortBy: RunnerSortByEnum) => void;
  isHarnessRace?: boolean;
}

export const RunnerSort = ({
  sortOrder,
  sortBy,
  onSortOrderChange,
  onSortByChange,
  isHarnessRace = false
}: RunnerSortProps) => {
  const isMobile = useMediaQuery(breakpoints.tablet.max.sm);
  const [isSortModalOpen, setIsSortModalOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(SORT_OPTION_DEFAULT.label);

  const sortOptions = isHarnessRace
    ? sortOptionsHarness
    : sortOptionsNotHarness;

  useEffect(() => {
    const newSelectedLabel = (
      sortOptions.optionsBasic.find((option) => option.value === sortBy) ||
      sortOptions.optionsAdvanced.find((option) => option.value === sortBy) ||
      sortOptions.optionsPerformance.find((option) => option.value === sortBy)
    )?.label;

    if (newSelectedLabel) {
      setSelectedLabel(newSelectedLabel);
    }
  }, [sortBy]);

  const iconName =
    sortOrder === RunnerSortOrderEnum.ASC ? "sortAscending" : "sortDescending";

  const renderSortContent = () => (
    <>
      <SegmentedControl
        size={isMobile ? "m" : "s"}
        selectedValue={sortOrder}
        onChange={(event) =>
          onSortOrderChange(event.nativeEvent.value as RunnerSortOrderEnum)
        }
        qaLabel="sort-segmented-control"
        m={isMobile ? "space-4" : "space-3"}
      >
        <SegmentedControlTab
          qaLabel={`sort-${RunnerSortOrderEnum.ASC}`}
          value={RunnerSortOrderEnum.ASC}
          end={<Icon name="sortAscending" size="s" marginLeft="space-1" />}
        >
          Ascending
        </SegmentedControlTab>
        <SegmentedControlTab
          qaLabel={`sort-${RunnerSortOrderEnum.DESC}`}
          value={RunnerSortOrderEnum.DESC}
          end={<Icon name="sortDescending" size="s" marginLeft="space-1" />}
        >
          Descending
        </SegmentedControlTab>
      </SegmentedControl>
      <OptionGroupsBox>
        {Object.entries(sortOptions).map(
          ([groupKey, groupValues], groupIdx) => (
            <OptionGroup key={groupKey} hasSeparator={groupIdx > 0}>
              {groupValues.map((option: SortOption) => (
                <Option
                  key={option.value}
                  label={option.label}
                  value={option.value}
                  qaLabel={option.value}
                />
              ))}
            </OptionGroup>
          )
        )}
      </OptionGroupsBox>
    </>
  );

  if (isMobile) {
    return (
      <>
        <Button
          qaLabel="runner-sort"
          size="m"
          variant="tertiary"
          iconPosition="end"
          icon={iconName}
          onPress={() => setIsSortModalOpen(true)}
        >
          Sort
        </Button>
        <Modal
          isOpen={isSortModalOpen}
          title="Sort by"
          type="flexible"
          onClose={() => setIsSortModalOpen(false)}
          p={0}
        >
          <>
            <SortContext.Provider
              value={{
                isDropdownOpen: isSortModalOpen,
                selectedValue: sortBy,
                onChange: (value) => onSortByChange(value as RunnerSortByEnum),
                layer: 1
              }}
            >
              {renderSortContent()}
            </SortContext.Provider>
            <ModalButtonContainer>
              <Button
                variant="primary"
                qaLabel="runner-sort-close-modal-button"
                onPress={() => setIsSortModalOpen(false)}
                isStretched
              >
                Close
              </Button>
            </ModalButtonContainer>
          </>
        </Modal>
      </>
    );
  }

  return (
    <Sort
      qaLabel="runner-sort"
      buttonLabel="Sort by"
      selectedLabel={selectedLabel}
      selectedValue={sortBy}
      onChange={(value) => onSortByChange(value as RunnerSortByEnum)}
      iconName={iconName}
      ml="space-1"
      width="170px"
      isContentCustom
    >
      <AnimatedDropdown qaLabel="runner-sort-dropdown" paddingTop="space-4">
        {renderSortContent()}
      </AnimatedDropdown>
    </Sort>
  );
};

export default RunnerSort;
